import type { Id } from "@Aspecto/backend/convex/_generated/dataModel";
import { api } from "@Aspecto/backend/convex/_generated/api";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Calendar,
  ChevronDown,
  Clock,
  ExternalLink,
  Loader2,
  MapPin,
  UserCheck,
  UserMinus,
  Users,
} from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AppPageContent } from "@/components/layout/app-page";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { EVENT_TYPES } from "@/lib/event-types";
import { formatDate, formatTime } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/app/events/")({
  component: EventsPage,
});

type EventFilter = "upcoming" | "past" | "all";

const FILTER_LABELS: Record<EventFilter, string> = {
  upcoming: "Upcoming",
  past: "Past",
  all: "All Events",
};

function getEventEndTimestamp(event: {
  eventDate: number;
  eventEndDate?: number;
  eventEndTime?: string;
  eventTime: string;
}): number {
  const endDate = event.eventEndDate ?? event.eventDate;
  const endTime = event.eventEndTime ?? event.eventTime;
  const [h, m] = endTime.split(":").map(Number);
  return endDate + (h ?? 0) * 3600000 + (m ?? 0) * 60000;
}

function filterEvents<
  T extends { eventDate: number; eventEndDate?: number; eventEndTime?: string; eventTime: string },
>(events: readonly T[], filter: EventFilter, now: number): T[] {
  switch (filter) {
    case "upcoming":
      return [...events]
        .filter((e) => getEventEndTimestamp(e) >= now)
        .sort((a, b) => a.eventDate - b.eventDate);
    case "past":
      return [...events]
        .filter((e) => getEventEndTimestamp(e) < now)
        .sort((a, b) => b.eventDate - a.eventDate);
    case "all":
      return [...events].sort((a, b) => b.eventDate - a.eventDate);
  }
}

function EventsPage() {
  const [filter, setFilter] = useState<EventFilter>("upcoming");
  const [pendingEventId, setPendingEventId] = useState<string | null>(null);
  const nowRef = useRef(Date.now());

  const eventsQuery = convexQuery(api.events.getAllUserEvents, {});
  const { data: allEvents, isLoading } = useQuery(eventsQuery);
  const { data: filteredEvents = [] } = useQuery({
    ...eventsQuery,
    select: (events) => filterEvents(events, filter, nowRef.current),
  });

  const { mutate: attendEvent } = useMutation({
    mutationFn: useConvexMutation(api.events.attendEvent),
    onSuccess: () => {
      toast.success("You're attending!", {
        description: "You've been added to the attendee list.",
      });
    },
    onError: (error) => {
      toast.error("Failed to RSVP", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    },
    onSettled: () => setPendingEventId(null),
  });

  const { mutate: cancelAttendance } = useMutation({
    mutationFn: useConvexMutation(api.events.cancelAttendance),
    onSuccess: () => {
      toast.success("RSVP cancelled", {
        description: "You've been removed from the attendee list.",
      });
    },
    onError: (error) => {
      toast.error("Failed to cancel RSVP", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    },
    onSettled: () => setPendingEventId(null),
  });

  const handleAttend = (eventId: Id<"events">) => {
    setPendingEventId(eventId);
    attendEvent({ eventId });
  };

  const handleCancel = (eventId: Id<"events">) => {
    setPendingEventId(eventId);
    cancelAttendance({ eventId });
  };

  const now = nowRef.current;

  if (isLoading) {
    return (
      <AppPageContent>
        <PageHeader title="Events" description="All events across your groups" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader className="space-y-2">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </AppPageContent>
    );
  }

  return (
    <AppPageContent>
      <PageHeader title="Events" description="All events across your groups" />

      {!allEvents || allEvents.length === 0 ? (
        <EmptyState
          icon={Calendar}
          title="No events yet"
          description="Events from your groups will appear here."
        />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center">
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-sm text-muted-foreground shadow-xs">
                <Calendar className="h-4 w-4" />
                {FILTER_LABELS[filter]}
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="min-w-[var(--radix-dropdown-menu-trigger-width)]"
              >
                {(["upcoming", "past", "all"] as const).map((value) => (
                  <DropdownMenuItem
                    key={value}
                    onClick={() => setFilter(value)}
                    className={`text-sm ${filter === value ? "bg-accent" : ""}`}
                  >
                    {FILTER_LABELS[value]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {filteredEvents.length === 0 ? (
            <EmptyState
              icon={Calendar}
              title={filter === "past" ? "No past events" : "No upcoming events"}
              description={
                filter === "past"
                  ? "Past events will appear here after they've ended."
                  : "Check back later for upcoming events."
              }
            />
          ) : (
            filteredEvents.map((event) => {
              const isPast = getEventEndTimestamp(event) < now;
              const eventType = EVENT_TYPES[event.eventType];
              const EventTypeIcon = eventType.icon;

              const isPending = pendingEventId === event._id;

              return (
                <Card
                  key={event._id}
                  className={`transition-all duration-200 ${isPast ? "opacity-60" : ""}`}
                >
                  <CardHeader className="pb-2 sm:pb-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-base font-semibold sm:text-lg">
                            <Link
                              to="/app/groups/$groupId/events"
                              params={{ groupId: event.groupId }}
                              className="hover:underline"
                            >
                              {event.title}
                            </Link>
                          </h3>
                          {isPast && (
                            <Badge variant="secondary" className="bg-gray-100 text-gray-600">
                              Expired
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4 shrink-0" />
                            {formatDate(event.eventDate)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4 shrink-0" />
                            {formatTime(event.eventTime)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-4 w-4 shrink-0" />
                            {event.groupName}
                          </span>
                        </div>
                      </div>
                      <Badge variant="outline" className="shrink-0 gap-1 self-start">
                        <EventTypeIcon className="h-4 w-4" />
                        {eventType.label}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 sm:space-y-4">
                    {event.description && (
                      <p className="text-sm text-muted-foreground">{event.description}</p>
                    )}

                    <div className="flex flex-wrap gap-4 text-sm">
                      {event.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          {event.location}
                        </span>
                      )}
                      {event.meetingLink && (
                        <a
                          href={event.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-primary hover:underline"
                        >
                          <ExternalLink className="h-4 w-4" />
                          Join Meeting
                        </a>
                      )}
                    </div>

                    <div className="space-y-2 border-t pt-2 sm:space-y-3 sm:pt-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="text-sm">
                            {event.attendeeCount} attending
                            {event.attendeeLimit && ` / ${event.attendeeLimit} max`}
                          </span>
                          {event.isFull && (
                            <Badge variant="secondary" className="text-xs">
                              Full
                            </Badge>
                          )}
                        </div>

                        {!isPast && (
                          <>
                            {event.isAttending ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCancel(event._id)}
                                disabled={isPending}
                              >
                                {isPending ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <UserMinus className="mr-2 h-4 w-4" />
                                )}
                                {isPending ? "Cancelling..." : "Cancel RSVP"}
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => handleAttend(event._id)}
                                disabled={isPending || event.isFull}
                              >
                                {isPending ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <UserCheck className="mr-2 h-4 w-4" />
                                )}
                                {event.isFull ? "Event Full" : isPending ? "Joining..." : "Attend"}
                              </Button>
                            )}
                          </>
                        )}
                      </div>

                      {event.creator && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <span>Created by</span>
                          <Avatar className="h-5 w-5">
                            <AvatarImage src={event.creator.imageUrl} />
                            <AvatarFallback className="text-[10px]">
                              {event.creator.firstName?.[0]}
                              {event.creator.lastName?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <span>
                            {event.creator.firstName} {event.creator.lastName}
                          </span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}
    </AppPageContent>
  );
}
